import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'
import { useConfirmare } from '../../components/ui/Confirmare'
import { SetariAiUsageTab } from './SetariAiUsageTab'
import { RetriereTaskuri } from './RetriereTaskuri'

type Tab = 'config' | 'usage' | 'pricing'

export function SetariAiPage() {
  const [tab, setTab] = useState<Tab>('config')

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → AI / Anthropic</h1>

      <div className="flex gap-1 border-b border-b4l-border">
        <TabButton active={tab === 'config'} onClick={() => setTab('config')}>
          Configurare
        </TabButton>
        <TabButton active={tab === 'usage'} onClick={() => setTab('usage')}>
          Consum &amp; cost
        </TabButton>
        <TabButton active={tab === 'pricing'} onClick={() => setTab('pricing')}>
          Pricing modele
        </TabButton>
      </div>

      {tab === 'config' && <ConfigTab />}
      {tab === 'usage' && <SetariAiUsageTab />}
      {tab === 'pricing' && <PricingTabPlaceholder />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm transition ${
        active
          ? 'border-b-2 border-b4l-green font-semibold text-b4l-text'
          : 'text-b4l-text-muted hover:text-b4l-text'
      }`}
    >
      {children}
    </button>
  )
}

interface AiSettings {
  anthropicKeySet: boolean
  adminKeySet: boolean
  updatedAt: string | null
}

function ConfigTab() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.users:write')
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')

  const [apiKey, setApiKey] = useState('')
  const [adminKey, setAdminKey] = useState('')

  const query = useQuery<{ settings: AiSettings }>({
    queryKey: ['ai-settings'],
    queryFn: async () => (await api.get<{ settings: AiSettings }>('/settings/ai')).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => (await api.put('/settings/ai', { anthropicApiKey: apiKey })).data,
    onSuccess: () => {
      toast.success('Cheie Anthropic salvată')
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] })
      setApiKey('')
    },
    onError: () => toast.error('Eroare salvare'),
  })

  // Cheia Admin e separată de cea de inferență și serveste la altceva: citeste costul REAL
  // facturat din /v1/organizations/cost_report. Fara ea, ecranul de consum arata doar
  // apelurile CRM-ului — ~95% din total, restul fiind celelalte aplicatii de pe aceeasi cheie.
  const saveAdminMutation = useMutation({
    mutationFn: async () => (await api.put('/settings/ai', { adminApiKey: adminKey })).data,
    onSuccess: () => {
      toast.success('Cheie Admin salvată — costul facturat apare în „Consum & cost”')
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] })
      queryClient.invalidateQueries({ queryKey: ['ai-billed'] })
      setAdminKey('')
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Cheia trebuie să înceapă cu sk-ant-admin',
      ),
  })

  const removeMutation = useMutation({
    mutationFn: async () => (await api.delete('/settings/ai/anthropic-key')).data,
    onSuccess: () => {
      toast.success('Cheie Anthropic eliminată')
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] })
    },
    onError: () => toast.error('Eroare ștergere'),
  })

  const s = query.data?.settings

  return (
    <div className="space-y-4">
      <Card title="Stare">
        {s ? (
          <div className="space-y-2 text-sm">
            <div>
              <strong>Cheie Anthropic:</strong>{' '}
              {s.anthropicKeySet ? (
                <span className="text-b4l-green-dark">✓ configurată</span>
              ) : (
                <span className="text-b4l-red-dark">✗ nu e setată</span>
              )}
            </div>
            <div>
              <strong>Cheie Admin (cost facturat):</strong>{' '}
              {s.adminKeySet ? (
                <span className="text-b4l-green-dark">✓ configurată</span>
              ) : (
                <span className="text-b4l-text-muted">
                  — opțional; fără ea vezi doar consumul CRM-ului
                </span>
              )}
            </div>
            {s.updatedAt && (
              <div className="text-xs text-b4l-text-muted">
                Ultima actualizare: {new Date(s.updatedAt).toLocaleString('en-GB')}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        )}
      </Card>

      <Card
        title="Configurare cheie Anthropic API"
        description="Cheia e folosită pentru clasificarea automată email-uri → task-uri (Claude haiku-4-5). Obții o cheie de pe https://console.anthropic.com/."
      >
        <div className="space-y-3">
          <PasswordField
            label={
              s?.anthropicKeySet
                ? 'Cheie nouă (lasă gol pentru a păstra)'
                : 'Cheie API (sk-ant-...)'
            }
            placeholder="sk-ant-..."
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={!canWrite}
          />
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!apiKey.trim()}
              >
                Salvează
              </Button>
              {s?.anthropicKeySet && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (
                      confirm(
                        'Ești sigur că vrei să ștergi cheia? Task-urile auto vor înceta să mai fie create.',
                      )
                    )
                      removeMutation.mutate()
                  }}
                  loading={removeMutation.isPending}
                >
                  Șterge cheia
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card
        title="Cheie Admin — costul real facturat"
        description="Ecranul „Consum & cost” numără doar apelurile făcute din CRM. Pe aceeași cheie Anthropic mai rulează și alte aplicații interne, care nu raportează aici. Cu o cheie Admin, ecranul arată și totalul facturat de Anthropic, plus diferența față de consumul CRM-ului."
      >
        <div className="space-y-3">
          <PasswordField
            label={
              s?.adminKeySet
                ? 'Cheie Admin nouă (lasă gol pentru a păstra)'
                : 'Cheie Admin (sk-ant-admin01-...)'
            }
            placeholder="sk-ant-admin01-..."
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            disabled={!canWrite}
          />
          <p className="text-xs text-b4l-text-muted">
            E o cheie <b>diferită</b> de cea de mai sus: se creează din Console → Settings → API
            keys, secțiunea Admin keys, și există doar pe conturi de organizație. Pe un cont
            individual endpoint-ul de cost nu e disponibil.
          </p>
          {canWrite && (
            <Button
              onClick={() => saveAdminMutation.mutate()}
              loading={saveAdminMutation.isPending}
              disabled={!adminKey.trim()}
            >
              Salvează cheia Admin
            </Button>
          )}
        </div>
      </Card>

      <Card description="Cheia e păstrată criptată. Administratorii văd doar dacă e configurată (✓ / ✗), niciodată valoarea ei.">
        <p className="text-xs text-b4l-text-muted">
          <strong>Notă:</strong> dacă cheia lipsește, scan-urile email-uri rulează silent fără a
          crea task-uri. Ce a făcut ultima scanare se vede mai jos, la „Scanarea emailurilor”.
        </p>
      </Card>

      <ScanareEmailuri />
      {eAdmin && <RetriereTaskuri />}
    </div>
  )
}

interface StareScan {
  startedAt: string
  finishedAt: string | null
  scanned: number
  candidates: number
  classified: number
  tasksCreated: number
  tasksUpdated: number
  ignored: number
  errors: number
  diagnostics: string[]
}

/**
 * Scanarea emailurilor in task-uri — mutata aici din vechea pagina Task-uri (12.10.2026), care
 * a disparut. E o unealta de administrare a extractorului, nu a tabloului.
 */
function ScanareEmailuri() {
  const { confirma } = useConfirmare()
  /** Cand am pornit scanarea de aici; `null` = nu urmaresc nimic. */
  const [pornitLa, setPornitLa] = useState<number | null>(null)
  const urmaresc = pornitLa != null
  const stare = useQuery<{ status: StareScan | null }>({
    queryKey: ['scan-status'],
    queryFn: async () => (await api.get('/tasks/extractor/last-scan-status')).data,
    // Cat ruleaza o scanare pornita de aici, cifrele se improspateaza singure.
    refetchInterval: urmaresc ? 5_000 : false,
  })
  const scaneaza = useMutation({
    mutationFn: async () => (await api.post('/tasks/extractor/scan-now')).data,
    onSuccess: () => {
      toast.success('Scanarea a pornit. Task-urile noi apar la „De preluat” în 1–3 minute.')
      setPornitLa(Date.now() - 5_000)
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? 'Scanarea nu a putut porni.',
      ),
  })
  // Un email clasificat o data nu se mai trimite la Claude (asa a scazut costul de la ~$120 la
  // ~$7/luna). Reclasificarea anuleaza asta si are sens DOAR dupa o schimbare de prompt.
  const reclasifica = useMutation({
    mutationFn: async () =>
      (await api.post<{ count: number }>('/tasks/extractor/reset-seen?days=7')).data,
    onSuccess: (d) =>
      toast.success(`${d.count} emailuri vor fi reclasificate la scanarea următoare.`),
    onError: () => toast.error('Reclasificarea nu a putut fi pregătită.'),
  })
  const s = stare.data?.status
  // Scanarea s-a terminat: nu mai e nevoie de improspatare.
  useEffect(() => {
    if (pornitLa != null && s?.finishedAt && new Date(s.startedAt).getTime() >= pornitLa)
      setPornitLa(null)
  }, [pornitLa, s?.finishedAt, s?.startedAt])

  return (
    <Card
      title="Scanarea emailurilor"
      description="Emailurile care par o treabă devin task-uri la „De preluat”, în departamentul de care țin. Scanarea rulează singură; de aici o pornești acum."
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => scaneaza.mutate()} loading={scaneaza.isPending}>
            Scanează acum
          </Button>
          <Button
            variant="secondary"
            loading={reclasifica.isPending}
            onClick={async () => {
              if (
                await confirma({
                  titlu: 'Reclasifici emailurile din ultimele 7 zile?',
                  mesaj:
                    'Se trimit din nou la Claude și se plătesc din nou. Are sens doar după ce s-a schimbat felul în care se clasifică.',
                  buton: 'Reclasifică',
                })
              )
                reclasifica.mutate()
            }}
          >
            Reclasifică ultimele 7 zile
          </Button>
        </div>
        {s ? (
          <div className="space-y-2 text-xs text-b4l-text-muted">
            <p>
              Ultima scanare: pornită {new Date(s.startedAt).toLocaleString('ro-RO')}
              {s.finishedAt
                ? `, terminată ${new Date(s.finishedAt).toLocaleString('ro-RO')}`
                : ' — încă rulează…'}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
              <span>Scanate {s.scanned}</span>
              <span>Candidate {s.candidates}</span>
              <span>Clasificate {s.classified}</span>
              <span className="text-b4l-green-dark">Task-uri noi {s.tasksCreated}</span>
              <span>Actualizate {s.tasksUpdated}</span>
              <span>Ignorate {s.ignored}</span>
              <span className={s.errors > 0 ? 'font-semibold text-b4l-red-dark' : ''}>
                Erori {s.errors}
              </span>
            </div>
            {s.diagnostics.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-b4l-amber-dark">
                {s.diagnostics.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-xs text-b4l-text-muted">
            {stare.isLoading ? 'Se încarcă…' : 'Nicio scanare de la ultima pornire a serverului.'}
          </p>
        )}
      </div>
    </Card>
  )
}

function PricingTabPlaceholder() {
  return (
    <Card description="Prețurile modelelor AI folosite la calculul costului.">
      <p className="text-sm italic text-b4l-text-muted">
        Prețurile pe model, pe milion de tokeni (intrare / ieșire), sunt deocamdată fixe: Haiku 1 $
        / 5 $, Sonnet 3 $ / 15 $, Opus 15 $ / 75 $. Editarea lor din interfață va veni într-o
        versiune viitoare.
      </p>
    </Card>
  )
}
